import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthContextType, User, RegisterData } from '../types';
import { authService } from '../services/authService';
import { setupAxiosInterceptors } from '../utils/axiosInterceptor';
import { getCsrfToken } from '../utils/csrf';

// Session auth now lives in an httpOnly cookie, unreadable by JS — `token` in state is no
// longer a real credential, just a truthy sentinel kept so the many existing `if (!token)`
// guards scattered across the app keep working unchanged.
const AUTH_TOKEN_SENTINEL = 'session-cookie';

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'CLEAR_ERROR' };

const initialState: AuthState = {
  user: null,
  token: null,
  loading: true,
  error: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: null,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Setup axios interceptors for automatic token expiration handling.
  // Routes through the real logout() (defined below) instead of a bare dispatch so an
  // expired-session auto-logout gets the same account-switching-data cleanup as an
  // explicit logout — captured once on mount, which is safe here since logout() only
  // closes over `dispatch` (stable) and `authService` (module import), not over any
  // state that changes across renders.
  useEffect(() => {
    setupAxiosInterceptors(() => logout());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize auth state on mount. Previously this trusted a localStorage token
  // indefinitely with no live check; now there's no client-readable token to trust at
  // all, so this confirms the session cookie is actually still valid via the backend —
  // optimistically rendering the cached user first for a fast paint, then correcting
  // (or logging out) once the real check resolves.
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const userStr = localStorage.getItem('user');
        const cachedUser = userStr ? (JSON.parse(userStr) as User) : null;
        if (cachedUser) {
          dispatch({ type: 'LOGIN_SUCCESS', payload: { user: cachedUser, token: AUTH_TOKEN_SENTINEL } });
        }

        const profileResponse = await authService.getProfile();
        if (profileResponse.success && profileResponse.data) {
          localStorage.setItem('user', JSON.stringify(profileResponse.data));
          dispatch({ type: 'LOGIN_SUCCESS', payload: { user: profileResponse.data, token: AUTH_TOKEN_SENTINEL } });

          // Self-heal sessions created before the CSRF-cookie Domain fix: the httpOnly
          // auth cookie above proves this session is genuinely logged in, but if the
          // separate (non-httpOnly) csrf_token cookie isn't readable here, it's an old
          // host-only cookie from before the fix — every mutation would 403 with "CSRF
          // token missing or invalid" until this re-issues both cookies correctly scoped.
          if (!getCsrfToken()) {
            authService.refreshToken().catch(() => {});
          }
        } else {
          localStorage.removeItem('user');
          dispatch({ type: 'LOGOUT' });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        localStorage.removeItem('user');
        dispatch({ type: 'LOGOUT' });
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });

    try {
      const response = await authService.login(email, password);

      if (response.success && response.data) {
        const { user } = response.data;

        localStorage.setItem('user', JSON.stringify(user));

        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user, token: AUTH_TOKEN_SENTINEL }
        });
      } else {
        throw new Error(response.error || 'Login failed');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Login failed';
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: errorMessage
      });
      throw error;
    }
  };

  const register = async (userData: RegisterData): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });

    try {
      const response = await authService.register(userData);

      if (response.success && response.data) {
        const { user } = response.data;

        localStorage.setItem('user', JSON.stringify(user));

        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user, token: AUTH_TOKEN_SENTINEL }
        });
      } else {
        throw new Error(response.error || 'Registration failed');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Registration failed';
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: errorMessage
      });
      throw error;
    }
  };

  const loginWithCredentials = (user: User, _token: string): void => {
    // _token is a legacy param — the httpOnly cookie is already set server-side by
    // the time callers (e.g. Google sign-in) have a response to hand us.
    localStorage.setItem('user', JSON.stringify(user));
    dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token: AUTH_TOKEN_SENTINEL } });
  };

  const logout = (): void => {
    // 1. Clear core auth data
    localStorage.removeItem('user');
    // Active scan task ID isn't user-scoped — clear it so the next account on this
    // device doesn't auto-resume polling and see this user's scan results.
    localStorage.removeItem('potential_patterns_task_id');
    // Dashboard watchlist isn't user-scoped either — clear it so the next account
    // on this device doesn't inherit this user's (or admin's) watchlist coins.
    localStorage.removeItem('user_watchlist');

    // 2. Clear all user-specific local backups (Fix for account switching data leakage)
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('scanned_symbols_backup_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log(`[Auth] Cleared ${keysToRemove.length} local history backups.`);
    } catch (e) {
      console.error('Error clearing local backups:', e);
    }

    // 3. Clear state
    dispatch({ type: 'LOGOUT' });

    // 4. Notify other contexts (ScanContext, AnalysisContext, ...) that don't live
    // under AuthContext so they can clear their own in-memory + localStorage state —
    // otherwise the next account on this device can see this user's in-flight scan
    // or cached analysis results.
    window.dispatchEvent(new Event('lumina:logout'));

    // 5. Call logout service — this is now what actually ends the session
    // (clears the httpOnly cookie server-side, since the client can't).
    authService.logout().catch(console.error);
  };

  const updateUser = async (userData: Partial<User>): Promise<void> => {
    if (!state.user) {
      throw new Error('User not authenticated');
    }

    try {
      const response = await authService.updateProfile(userData);

      if (response.success && response.data) {
        const updatedUser = response.data;

        // Update localStorage
        localStorage.setItem('user', JSON.stringify(updatedUser));

        dispatch({
          type: 'UPDATE_USER',
          payload: updatedUser
        });
      } else {
        throw new Error(response.error || 'Update failed');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Update failed';
      throw new Error(errorMessage);
    }
  };

  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const setUser = (user: User): void => {
    // Update user in state and localStorage
    localStorage.setItem('user', JSON.stringify(user));
    dispatch({
      type: 'UPDATE_USER',
      payload: user
    });
  };

  const contextValue: AuthContextType = {
    user: state.user,
    token: state.token,
    loading: state.loading,
    error: state.error,
    login,
    loginWithCredentials,
    register,
    logout,
    updateUser,
    setUser,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
